import { Center, Flex, Image, Text, VStack, Box, HStack } from '@chakra-ui/react';
import Lottie from 'lottie-react';
import { useRef, useEffect, memo } from 'react';
import audio from 'src/asset/images/audio.png';
import waveData from 'src/asset/lottie-json/audio_wave.json';
import WatchVideo from '../HelperComponents/WatchVideo';
import useMediaOptions from './useMediaOptions';
import { noImageFoundText } from 'src/component/constants';

const WatchWithAudio = memo(({ exercise, setSelectedAnswer, disabled, ...props }) => {
  const lottieRef = useRef();
  const {
    mediaList,
    isPlaying,
    currentlyPlaying,
    onAudioPlay,
    stopAllAudio,
    registerAnimationRef,
    unregisterAnimationRef
  } = useMediaOptions({
    exercise,
    hasAudio: true
  });

  const selectedMedia = currentlyPlaying?.startsWith('audio-') 
    ? currentlyPlaying.replace('audio-', '') 
    : '';
  
  const blockName = 'watchWithAudio'

  // Register animation and handle cleanup
  useEffect(() => {
    if (selectedMedia) {
      registerAnimationRef(selectedMedia, lottieRef.current);
    }
    return () => {
      if (selectedMedia) {
        unregisterAnimationRef(selectedMedia);
      }
    };
  }, [selectedMedia, registerAnimationRef, unregisterAnimationRef]);

  const handleAnswerClick = (media) => {
    if (disabled) return;

    const audioKey = `audio-${media.key}`;
    
    // Toggle if clicking the current audio
    if (selectedMedia === media.key && isPlaying) {
      stopAllAudio();
      return;
    }

    // Play the audio
    setSelectedAnswer(media);
    onAudioPlay(media.value, audioKey);
  };

  return (
    <Box width="100%">
      <Flex
        direction={['column', 'column', 'row']}
        align="center"
        justify="center"
        gap={[6, 6, 10]}
        width="100%"
        maxWidth="1200px"
        margin="0 auto"
      >
        <Box flex={['1 0 100%', '1 0 100%', '0 1 60%']} maxWidth={['100%', '100%', '600px']}>
          <WatchVideo exercise={exercise} disabled={disabled} {...props} />
        </Box>

        <Box className={`options-container_${blockName}`} flex={['1 0 auto', '1 0 auto', '0 1 60%']} maxWidth={['100%', '100%', '500px']} p={[2, 2, 0]}>
          <VStack spacing={3} width="100%" align="center">
            {mediaList.map((media, i) => {
              const isSelected = selectedMedia === media.key && isPlaying;
              return (
                <Center
                  key={media.key}
                  onClick={() => !disabled && handleAnswerClick(media)}
                  border="1px solid"
                  borderColor={isSelected ? 'blue.500' : 'gray.200'}
                  bg={isSelected ? 'blue.50' : 'white'}
                  boxShadow={isSelected ? '0 0 0 2px #2367f0' : 'none'}
                  borderRadius="25px"
                  w={['100%', '100%', '200px']}
                  h="70px"
                  cursor={disabled ? 'not-allowed' : 'pointer'}
                  overflow="hidden"
                  opacity={disabled ? 0.7 : 1}
                  transition="all 0.2s"
                >
                  {isSelected ? (
                    <Lottie
                      animationData={waveData}
                      loop
                      lottieRef={lottieRef}
                      style={{ width: 140, height: 70 }}
                    />
                  ) : (
                    <HStack justifyContent='center' width={'120px'}>
                      <Image mr={3} boxSize="20px" src={audio} alt={noImageFoundText} />
                      <Text color="black" fontSize="sm" fontWeight="medium">
                        Audio {i + 1}
                      </Text>
                    </HStack>
                  )}
                </Center>
              );
            })}
          </VStack>
        </Box>
      </Flex>
    </Box>
  );
});

export default WatchWithAudio;